'use client';

import { IExchangeRate } from '@/api/types/public';
import Accordion from '@/components/Accordion';
import Breadcrumbs from '@/components/Breadcrumbs';
import Select from '@/components/Form/Select';
import Popup from '@/components/Popup';
import { useStore } from '@/providers/app-store-provider';
import { env } from '@/utils/env';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { MenuItem, useMediaQuery } from '@mui/material';
import { CollapseArrowDownOutlined } from '@zeelool/icon';
import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';

// 接口类型定义
interface ShippingInfo {
  shippingAndReturns: {
    title: string;
    subtitle: string;
    sections: Array<{
      icon: string;
      text: string;
      linkType: 'anchor' | 'link';
      link: string;
    }>;
  };
  shipping: {
    title: string;
    subtitle: string;
  };
  warrantyAndReturns: {
    title: string;
    subtitle: string;
    details: Array<{
      title: string;
      description: string | string[];
      boldParts?: string[];
      links?: Array<{
        text: string;
        url: string;
      }>;
    }>;
    additionalInfo: {
      freeStandardShipping: {
        title: string;
        description: string | string[];
        boldParts?: string[];
        conditions: Array<{
          title: string;
          description: string | string[];
          boldParts?: string[];
        }>;
      };
      shippingTerms: {
        title: string;
        description: string | string[];
        boldParts?: string[];
        items: Array<{
          title: string;
          description: string | string[];
          boldParts?: string[];
          items?: string[];
        }>;
      };
    };
  };
  faq: {
    title: string;
    questions: Array<{
      question: string;
      description: string | string[];
      boldParts?: string[];
      links?: Array<{
        text: string;
        url: string;
      }>;
    }>;
  };
}

interface Props {
  shippingInfo: ShippingInfo;
  exchangeRate: IExchangeRate;
}

export default function ShippingTrackingClient({ shippingInfo, exchangeRate }: Props) {
  const t = useTranslations('shipping-tracking');
  // 状态管理
  const [country, setCountry] = useState(env.NEXT_PUBLIC_ADDRESS_COUNTRY_ID);
  const [openFlag, setOpenFlag] = useState(false);
  const [activeNames, setActiveNames] = useState<string[]>([]);
  const { control } = useForm();
  const countryList = useStore((state) => state.countryList);

  const isMobile = useMediaQuery('(max-width:1024px)');

  // 获取当前货币信息
  const currency = useStore((state) => state.currency);

  // 计算当前选择国家的信息
  const infor = countryList.find((item) => item.country_id === country);

  // 获取当前货币的汇率信息
  const currencyInfo = exchangeRate.find((item) => item.name === currency.name);

  // 汇率转换函数
  const addRate = (value: number, _currencyInfo: IExchangeRate[number]) => {
    if (!_currencyInfo) return value;
    try {
      const price = (value * Number(_currencyInfo.exchange_rate)).toFixed(2);
      return parseFloat(price);
    } catch {
      return value;
    }
  };

  // 格式化文本内容
  const formatText = (textObj: any) => {
    if (!textObj || !textObj.description) {
      return '';
    }

    let { description } = textObj;
    const { boldParts = [] } = textObj;

    if (Array.isArray(description)) {
      description = description.join('<br/>');
    }

    let formattedText = description;
    if (Array.isArray(boldParts)) {
      boldParts.forEach((bold: string) => {
        const plainRegex = new RegExp(bold, 'g');
        formattedText = formattedText.replace(
          plainRegex,
          `<span class="font-bold text-gray-800">${bold}</span>`,
        );
      });
    }

    // 如果存在链接，处理链接
    if (textObj.links) {
      textObj.links.forEach((link) => {
        // 直接在文本中查找并替换链接文本
        formattedText = formattedText.replace(
          link.text,
          `<a href="${link.url}"  data-url="${link.url}" data-text="${link.type}" class="custom-link text-primary-purple underline">${link.text}</a>`,
        );
      });
    }

    return formattedText;
  };

  const handleLinkClick = (e: any) => {
    const link = e.target.closest('.custom-link');

    if (link) {
      // 获取链接数据
      e.preventDefault();

      const { url } = link.dataset;
      const type = link.dataset.text;
      if (type === 'anchor') {
        const anchor = document.querySelector(url);
        if (anchor) {
          // 使用 scrollIntoView，CSS scroll-margin-top 会自动处理偏移
          anchor.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        window.open(url, '_blank');
      }
    }
  };

  // 处理链接点击
  const handleLink = (item: any) => {
    if (item.linkType === 'anchor') {
      const anchor = document.querySelector(item.link);
      if (anchor) {
        // 使用 scrollIntoView，CSS scroll-margin-top 会自动处理偏移
        anchor.scrollIntoView({ behavior: 'smooth' });
      }
    }
    if (item.linkType === 'link') {
      window.open(item.link, '_blank');
    }
  };

  return (
    <div className="w-full bg-white font-be text-primary-black">
      {/* 头部容器 */}
      <div className="bg-[rgba(70,58,232,.1)] bg-opacity-10 relative px-[20px] md:px-[140px] lg:px-[160px] 2xl:[180px] pt-[56px] md:pt-[72px] pb-[32px] md:pb-[48px] text-center">
        {/* 导航容器 */}
        {isMobile && (
          <div className="absolute left-[14px] top-[10px] z-[99] text-primary-black text-[12px] font-br">
            <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: t('shippingReturns') }]} />
          </div>
        )}

        {/* 标题部分 */}
        <div className="mb-[20px] md:mb-[48px]">
          <h1 className="text-[24px] md:text-[40px] text-primary-black mb-[8px] md:mb-[16px]">
            {shippingInfo.shippingAndReturns.title}
          </h1>
          <h3 className="text-[13px] md:text-[16px] font-br">
            {shippingInfo.shippingAndReturns.subtitle}
          </h3>
        </div>

        {/* 图标和文字卡片 */}
        <div className="grid grid-cols-1 gap-16 md:grid-cols-4">
          {shippingInfo.shippingAndReturns.sections.map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-[6px] md:rounded-[12px] p-[17px_24px] md:p-[62px] flex justify-start items-center md:flex-col md:items-center cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => handleLink(item)}
            >
              <img className="md:mb-[20px] w-32 h-32 md:w-56 md:h-56" src={item.icon} />
              <h3 className="text-[16px] md:text-[20px] ml-[16px] md:ml-0 mb-0 font-be whitespace-nowrap overflow-hidden text-ellipsis">
                {item.text}
              </h3>
            </div>
          ))}
        </div>
      </div>

      {/* 运输容器 */}
      <div
        id="shipping"
        className="py-[40px] md:py-[72px] px-[14px] md:px-[200px] xl:px-[420px] text-center min-w-[375px]"
        style={{ scrollMarginTop: 'calc(var(--global-header-height, 100px) + 20px)' }}
      >
        <h1 className="text-[24px] md:text-[40px] text-primary-black mb-[8px] md:mb-[16px]">
          {shippingInfo.shipping.title}
        </h1>
        <h3 className="text-[13px] md:text-[16px] font-br mb-[20px] leading-[19px]">
          {shippingInfo.shipping.subtitle}
        </h3>

        {/* 国家选择器 */}
        {!isMobile ? (
          // PC端使用select选择器
          <Select
            control={control}
            name="country"
            defaultValue={'United States'}
            value={infor?.country}
            onChange={(e) => {
              const _country = countryList.find((item) => item.country === e.target.value);
              if (_country) {
                setCountry(_country.country_id);
              }
            }}
            className="w-full h-[45px] px-[14px] rounded-[6px]  bg-white text-[14px] font-be cursor-pointer bg-[right_14px_center] bg-no-repeat"
          >
            {countryList.map((item, index) => (
              <MenuItem
                key={index}
                value={item.country}
                className="text-primary-black py-8 cursor-pointer"
              >
                {item.country}
              </MenuItem>
            ))}
          </Select>
        ) : (
          <div
            className="flex justify-between items-center w-full h-[45px] px-[14px] leading-[45px] rounded-[6px] border border-[#d4d4d4] cursor-pointer mb-[15px] hover:border-primary-purple transition-colors"
            onClick={() => setOpenFlag(true)}
          >
            <span className="text-[14px] font-be">{infor?.country}</span>
            <CollapseArrowDownOutlined
              className={`w-[16px] h-[16px] transition-transform duration-300 ${openFlag ? 'rotate-180' : ''}`}
            />
          </div>
        )}

        {/* 运输方式表格 */}
        {infor && (
          <table className="w-full text-[12px] md:text-[16px] border-separate border-spacing-0 mb-[10px]">
            <thead>
              <tr>
                <th className="w-[33.33%] h-[45px] md:h-[70px] border border-[#d4d4d4] text-center bg-[#f7f7f7] rounded-tl-[6px] md:rounded-tl-[12px] font-normal font-be">
                  {t('Shipping')}
                  <br />
                  {t('Method')}
                </th>
                <th className="w-[33.33%] h-[45px] md:h-[70px] border border-[#d4d4d4] text-center bg-[#f7f7f7] border-l-0 font-normal font-be">
                  {t('Estimated')}
                  <br />
                  {t('Delivery')}
                </th>
                <th className="w-[33.33%] h-[45px] md:h-[70px] border border-[#d4d4d4] text-center bg-[#f7f7f7] rounded-tr-[6px] md:rounded-tr-[12px] border-l-0 font-normal font-be">
                  {t('shippingFee')}
                </th>
              </tr>
            </thead>
            <tbody>
              {/* 标准运输 */}
              <tr>
                <td className="w-[33.33%] h-[48px] md:h-[70px] border border-[#d4d4d4] text-center border-t-0 font-br">
                  {t('standardShipping')}
                </td>
                <td className="w-[33.33%] h-[48px] md:h-[70px] border border-[#d4d4d4] text-center border-t-0 border-l-0 font-br">
                  {infor.ordinary === 1
                    ? `${infor.ordinary_time_min}-${infor.ordinary_time_max} Days`
                    : 'Not Available'}
                </td>
                <td className="w-[33.33%] h-[48px] md:h-[70px] border border-[#d4d4d4] text-center border-t-0 border-l-0 font-br">
                  {infor.ordinary === 1 ? (
                    <p className="mb-0 px-[10px] md:px-[20px] leading-[15px] md:leading-[22px]">
                      {formatPriceNumberWithSymbol(
                        addRate(Number(infor.ordinary_price), currencyInfo).toString(),
                        currencyInfo,
                      )}
                    </p>
                  ) : (
                    <p className="mb-0 px-[10px] md:px-[20px] leading-[15px] md:leading-[22px]">
                      /
                    </p>
                  )}
                </td>
              </tr>

              {/* 高级运输 */}
              <tr>
                <td className="w-[33.33%] h-[48px] md:h-[70px] border border-[#d4d4d4] text-center border-t-0 font-br">
                  {t('advancedShipping')}
                </td>
                <td className="w-[33.33%] h-[48px] md:h-[70px] border border-[#d4d4d4] text-center border-t-0 border-l-0 font-br">
                  {infor.advanced === 1
                    ? `${infor.advanced_time_min}-${infor.advanced_time_max} Days`
                    : 'Not Available'}
                </td>
                <td className="w-[33.33%] h-[48px] md:h-[70px] border border-[#d4d4d4] text-center border-t-0 border-l-0 font-br">
                  {infor.advanced === 1 ? (
                    <p className="mb-0 px-[10px] md:px-[20px] leading-[15px] md:leading-[22px]">
                      {formatPriceNumberWithSymbol(
                        addRate(Number(infor.advanced_price), currencyInfo).toString(),
                        currencyInfo,
                      )}
                    </p>
                  ) : (
                    <p className="mb-0 px-[10px] md:px-[20px] leading-[15px] md:leading-[22px]">
                      /
                    </p>
                  )}
                </td>
              </tr>

              {/* 商业快递 */}
              <tr>
                <td className="w-[33.33%] h-[48px] md:h-[70px] border border-[#d4d4d4] text-center border-t-0  rounded-bl-[6px] md:rounded-bl-[12px] font-br">
                  {t('businessExpress')}
                </td>
                <td className="w-[33.33%] h-[48px] md:h-[70px] border border-[#d4d4d4] text-center border-t-0 border-l-0  font-br">
                  {infor.business === 1
                    ? `${infor.business_time_min}-${infor.business_time_max} Days`
                    : 'Not Available'}
                </td>
                <td className="w-[33.33%] h-[48px] md:h-[70px] border border-[#d4d4d4] text-center border-t-0 border-l-0  rounded-br-[6px] md:rounded-br-[12px] font-br">
                  {infor.business === 1 ? (
                    <p className="mb-0 px-[10px] md:px-[20px] leading-[15px] md:leading-[22px]">
                      {formatPriceNumberWithSymbol(
                        addRate(Number(infor.business_price), currencyInfo).toString(),
                        currencyInfo,
                      )}
                    </p>
                  ) : (
                    <p className="mb-0 px-[10px] md:px-[20px] leading-[15px] md:leading-[22px]">
                      /
                    </p>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      {/* 保修和退货 */}
      <div className="md:px-[140px] lg:px-[160px] 2xl:[180px] px-14">
        {/* 标题和副标题 */}
        <div className="text-center mb-[32px] md:mb-[48px]">
          <h2 className="text-[24px] md:text-[40px] text-primary-black mb-[8px] md:mb-[16px]">
            {shippingInfo.warrantyAndReturns.title}
          </h2>
          <p className="mb-[10px] px-[28px] text-[13px] md:text-[16px] font-br leading-[19px] md:leading-[19px]">
            {shippingInfo.warrantyAndReturns.subtitle}
          </p>
        </div>

        {/* 详情部分 */}
        <div className="flex flex-col md:flex-row gap-16 md:gap-32 mb-[60px] md:mb-[92px]">
          {shippingInfo.warrantyAndReturns.details.map((detail, index) => (
            <div
              key={index}
              className="bg-[#ecebfd] p-[19px_16px] md:p-[46px_42px_46px_54px] rounded-[12px] flex-1"
            >
              <h3 className="text-[16px] md:text-[24px] font-be mb-[10px] md:mb-[24px]">
                {detail.title}
              </h3>
              <p
                className="text-[13px] md:text-[16px] leading-[20px] md:leading-[24px] font-br"
                dangerouslySetInnerHTML={{ __html: formatText(detail) }}
              />
            </div>
          ))}
        </div>

        {/* 附加信息 */}
        <div
          id="free-shipping"
          className="flex flex-col px-14 md:flex-row gap-6 md:gap-12 mb-[60px] md:mb-[90px] text-[13px] md:text-[16px] leading-[20px] md:leading-[24px]"
          style={{ scrollMarginTop: 'calc(var(--global-header-height, 100px) + 20px)' }}
        >
          {/* 免费标准运输 */}
          <div className="flex-1 md:pr-6">
            <h4 className="text-[18px] md:text-[24px] mb-[12px] font-be">
              {shippingInfo.warrantyAndReturns.additionalInfo.freeStandardShipping.title}
            </h4>
            <div className="w-full h-[1px] bg-[#d4d4d4] mb-[12px] md:mb-[20px]" />
            <p
              className="mb-[12px] text-[13px] md:text-[16px] leading-[20px] md:leading-[24px] font-br text-primary-text"
              dangerouslySetInnerHTML={{
                __html: formatText(
                  shippingInfo.warrantyAndReturns.additionalInfo.freeStandardShipping,
                ),
              }}
            />
            <ul className="list-disc pl-[16px] font-br text-primary-text">
              {shippingInfo.warrantyAndReturns.additionalInfo.freeStandardShipping.conditions.map(
                (condition, index) => (
                  <li key={index} className="mb-[8px]">
                    <span className="font-bold">{condition.title}:</span>
                    <span dangerouslySetInnerHTML={{ __html: formatText(condition) }} />
                  </li>
                ),
              )}
            </ul>
          </div>

          {/* 运输条款 */}
          <div className="flex-1 md:pl-6">
            <h4 className="text-[18px] md:text-[24px] mb-[12px] font-be">
              {shippingInfo.warrantyAndReturns.additionalInfo.shippingTerms.title}
            </h4>
            <div className="w-full h-[1px] bg-[#d4d4d4] mb-[12px] md:mb-[20px]" />
            <p
              className="mb-[12px] text-[13px] md:text-[16px] leading-[20px] md:leading-[24px] font-br text-primary-text"
              dangerouslySetInnerHTML={{
                __html: formatText(shippingInfo.warrantyAndReturns.additionalInfo.shippingTerms),
              }}
            />
            <ul className="list-disc pl-[16px] font-br text-primary-text">
              {shippingInfo.warrantyAndReturns.additionalInfo.shippingTerms.items.map(
                (item, index) => (
                  <li key={index}>
                    <div className="font-bold mb-[8px] mt-[16px]">{item.title}</div>
                    <span dangerouslySetInnerHTML={{ __html: formatText(item) }} />
                    {item.items && (
                      <ol className="list-decimal pl-[16px] mt-[8px]">
                        {item.items.map((subItem, subIndex) => (
                          <li key={subIndex}>
                            <div>{subItem}</div>
                          </li>
                        ))}
                      </ol>
                    )}
                  </li>
                ),
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* FAQ容器 */}
      <div className="py-[32px] md:py-[72px] md:px-[140px] lg:px-[160px] 2xl:[180px] xl:px-[448px] bg-[#f4f4f4] text-center px-14">
        <h4 className="text-[20px] md:text-[32px] mb-[20px] md:mb-[32px] font-be">
          {shippingInfo.faq.title}
        </h4>

        <Accordion
          items={shippingInfo.faq.questions.map((item, index) => ({
            key: index.toString(),
            title: `${index + 1}. ${item.question}`,
            content: (
              <div
                className="bg-[#f4f4f4] text-[14px] text-primary-text font-br text-left"
                onClick={handleLinkClick}
                dangerouslySetInnerHTML={{ __html: formatText(item) }}
              />
            ),
          }))}
          activeKey={activeNames}
          onChange={(keys) => {
            const newActiveNames = Array.isArray(keys) ? keys : [keys];
            setActiveNames(newActiveNames);
          }}
          accordion={false}
          borderless
          sx={{
            borderTop: '1px solid #d4d4d4 !important',
            overflow: 'hidden',
            borderRadius: '0px !important',
            backgroundColor: '#f4f4f4',

            '& .MuiAccordion-root': {
              marginTop: '16px',
              borderTop: '1px solid #d4d4d4',
              paddingTop: '25px',
              backgroundColor: '#f4f4f4',
              boxShadow: 'none',

              '&:before': {
                display: 'none',
              },
              '&:last-child': {
                borderBottom: '1px solid #d4d4d4',
                paddingBottom: '24px',
              },
            },
            '& .MuiAccordionSummary-root': {
              marginTop: '12px',
              padding: 0,
              height: '38px',
              minHeight: '38px',
              backgroundColor: '#f4f4f4',
              '& .MuiAccordionSummary-content': {
                margin: 0,
                fontSize: '14px',
                '@media (min-width: 768px)': {
                  fontSize: '16px',
                },
                fontFamily: 'var(--font-bb)',
                lineHeight: '20px',
              },
            },
            '& .MuiAccordionDetails-root': {
              padding: 0,
              backgroundColor: '#f4f4f4',
            },
          }}
        />
      </div>

      {/* 移动端国家选择器弹窗 */}
      {isMobile && (
        <Popup
          open={openFlag}
          onClose={() => setOpenFlag(false)}
          title="Select Country"
          position="bottom"
          height={400}
          round
          borderRadius={16}
        >
          <div className="overflow-y-auto">
            {countryList.length > 0 ? (
              countryList.map((item, index) => (
                <div
                  key={index}
                  className={`py-4 px-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 active:bg-gray-100 transition-colors ${
                    item.country === country ? 'bg-blue-50 border-blue-200' : ''
                  }`}
                  onClick={() => {
                    setCountry(item.country_id);
                    setOpenFlag(false);
                  }}
                >
                  <span
                    className={`text-[14px] font-be ${
                      item.country === country ? 'text-blue-600' : 'text-primary-black'
                    }`}
                  >
                    {item.country}
                  </span>
                </div>
              ))
            ) : (
              <div className="py-8 text-center">
                <span className="text-[14px] text-gray-500">No countries available</span>
              </div>
            )}
          </div>
        </Popup>
      )}
    </div>
  );
}
